import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { dematerialize, materialize, Observable, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import { HttpRequestType } from '@shared/http/http.service';
import { getMockMenu } from '@src/testing/data-mocks/menu/menu.mock';
import { getMockStepDataWithCertificateButton } from '@src/testing/data-mocks/steps/stepData-certificate-button.mock';
import { EntityStatus } from '@shared/models/menu.model';
import { getMockStepDataBlockerStep } from '@src/testing/data-mocks/steps/stepData-blocker-step.mock';
import { getMockLifemapLogBugUserNotCOmpleteLifemap } from '@src/testing/data-mocks/lifemapLogs/lifemapLogs.mock';

type StatusCodeSupported = -1 | 0 | 200 | 401 | 429 | 500 | 503;

interface Response {
  status: StatusCodeSupported | undefined;
  body?: any;
  delay?: number;
  error?: string;
}

@Injectable()
export class MockHttpInterceptor implements HttpInterceptor {
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const urlWithParams: string = request.urlWithParams;
    const method: HttpRequestType = request.method as HttpRequestType;
    const requestBody: any = request.body;

    const response: Response = this.buildResponseForSupportedUrl(request);

    const responseStatusCode: StatusCodeSupported | undefined = response.status;
    if (responseStatusCode !== undefined) {
      // eslint-disable-next-line no-console
      console.log(
        `\x1b[31m MockHttpInterceptor MOCK RESPONSE\x1b[0m - \x1b[32m${method}\x1b[0m - \x1b[41m${urlWithParams}\x1b[0m - responseBody:`,
        response.body,
        `  requestBody=`,
        requestBody,
      );
      const delayResponse: number = response.delay ? response.delay : 20;
      if (responseStatusCode === 200) {
        return new Observable((resp) => {
          resp.next(new HttpResponse(response));
          resp.complete();
        }).pipe(delay(delayResponse));
      } else if (responseStatusCode === 500 || responseStatusCode === 503) {
        return throwError(
          () =>
            new HttpErrorResponse({
              status: responseStatusCode,
              statusText: 'Internal server error',
              error: {
                message: 'Internal server error',
              },
            }),
        ).pipe(materialize(), delay(delayResponse), dematerialize());
      } else if (responseStatusCode === 401) {
        return throwError(
          () =>
            new HttpErrorResponse({
              status: 401,
              statusText: 'token_expired',
              error: 'token_expired',
            }),
        ).pipe(materialize(), delay(delayResponse), dematerialize());
      } else if (responseStatusCode === 429) {
        return throwError(
          () =>
            new HttpErrorResponse({
              status: 429,
              statusText: 'Too many login attempts. Try again later.',
              error: 'Too many login attempts. Try again later.',
            }),
        ).pipe(materialize(), delay(delayResponse), dematerialize());
      } else if (responseStatusCode === -1 || responseStatusCode === 0) {
        return throwError(
          () =>
            new HttpErrorResponse({
              status: responseStatusCode,
              statusText: 'Unknown Error',
              error: 'Http failure response for /api/program_data: 0 Unknown Error',
            }),
        ).pipe(materialize(), delay(delayResponse), dematerialize());
      }
    }

    // All unhandled request goes through
    // eslint-disable-next-line no-console
    console.log(
      `\x1b[36m MockHttpInterceptor NOT Intercepting request \x1b[0m - \x1b[32m${method}\x1b[0m ${urlWithParams}`,
    );
    return next.handle(request);
  }

  private buildResponseForSupportedUrl(request: HttpRequest<any>): Response {
    const urlWithParams: string = request.urlWithParams;
    const method: HttpRequestType = request.method as HttpRequestType;
    const requestBody: any = request.body;

    return this.buildResponse(urlWithParams, method, requestBody);
  }

  /**
   * buildResponse
   * @param urlWithParams: the url of the request sent to the back end
   * @param method: HttpRequestType
   * @param requestBody: JSON payload
   * @return: Response
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private buildResponse(urlWithParams: string, method: HttpRequestType, requestBody: any): Response {
    if (method === HttpRequestType.GET) {
      /** Examples
         - Return data from mock data
             if (/step/.test(urlWithParams)) {
               const params: string = urlWithParams.split('?')[1];
               if (params === 'slug=/potentialife-course/cycle-1/lifemap-1/step-1') {
                 return {
                   status: 200,
                   body: getMockStepDataOnlyVideo(),
                 };
               } else if (params === 'slug=/potentialife-course/cycle-1/module-2/step-5') {
                 return {
                   status: 200,
                   body: getMockStepDataCheckBox(),
                 };
               } else if (params === 'slug=/potentialife-course/cycle-1/lifemap-1/step-2') {
                 return {
                  status: 401,
                 };
               }
             }

         - Return an Error:
             return {
                status: 400,
                errors: [{ detail: ['whatTheFuckIsThis', { automation_names: ['Automation 1', 'Automation 2'] }] }],
              };
       */
      if (/menu/.test(urlWithParams)) {
        return {
          status: 200,
          body: getMockMenu(),
        };
      }
      if (/lifemapLogs\/level\/[1-3]/.test(urlWithParams)) {
        return {
          status: 200,
          // body: [],
          // body: getMockLifeMapLogs(),
          body: getMockLifemapLogBugUserNotCOmpleteLifemap(),
        };
      }
      if (/simulateError/.test(urlWithParams)) {
        return {
          status: 500,
        };
      }
      // if (/participants/.test(urlWithParams)) {
      //   return {
      //     status: 429,
      //   };
      // }
      // if (/menu/.test(urlWithParams)) {
      //   return {
      //     status: 500,
      //   };
      // }
      if (/step/.test(urlWithParams)) {
        const params: string = urlWithParams.split('?')[1];
        if (params === 'slug=/potentialife-course/cycle-1/module-2/step-2') {
          return {
            status: 500,
          };
        }
        if (params === 'slug=/potentialife-course/cycle-1/module-10-1/step-6') {
          return {
            status: 200,
            body: getMockStepDataWithCertificateButton(635, EntityStatus.completed),
            delay: 5,
          };
        }
        // if (params === 'slug=/potentialife-course/cycle-3/module-30-1/step-3') {
        //   return {
        //     status: 200,
        //     body: getMockStepDataWithCertificateButton(495, EntityStatus.locked),
        //     delay: 5,
        //   };
        // }
        if (params === 'slug=/potentialife-course/cycle-3/lifemap-3/step-5') {
          return {
            status: 200,
            body: getMockStepDataBlockerStep(),
          };
        }
      }

      // if (/program_data/.test(urlWithParams)) {
      //   return {
      //     status: 401,
      //   };
      // }
    } else if (method === HttpRequestType.POST) {
      /**
        Examples
           - Return an error
               if (/program_data/.test(urlWithParams)) {
                  return {
                    status: 500,
                  };
               }
       */
      if (/program_data/.test(urlWithParams)) {
        return {
          status: 200,
        };
      }
      // if (/authenticate/.test(urlWithParams)) {
      //   return {
      //     status: 500,
      //   };
      // }
    } else if (method === HttpRequestType.DELETE) {
      // Handle DELETE REQUESTS
    } else if (method === HttpRequestType.PUT) {
      // Handle PUT REQUESTS
      // if (/lifemapLogs\/level\/[1-3]/.test(urlWithParams)) {
      //   return {
      //     status: 401,
      //   };
      //   // return {
      //   //   status: 200,
      //   //   body: mockBackEndResponseToStoreLifeMapLogs(requestBody as LifemapLog[]),
      //   // };
      // }
    }

    // Don't mock the request
    return {
      status: undefined,
    };
  }
}
